import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Plane, Heart, Briefcase, MessageCircle } from 'lucide-react';
import { Button } from '../components/Button';
export function GetInvolved() {
  const options = [{
    icon: <Heart size={48} data-id="element-192" />,
    title: 'Pray',
    description: 'Join our prayer team and intercede for communities, projects, and local leaders.',
    details: ['Pray for Local Leaders: Lift up pastors, teachers, and volunteers as they serve in remote villages', 'Pray for Projects: Cover new wells and solar installations with prayer for safety and success', 'Pray for the Global Church: Ask God to bring lasting transformation beyond any one project'],
    cta: 'Join Prayer List',
    href: '#prayer'
  }, {
    icon: <Briefcase size={48} data-id="element-193" />,
    title: 'Volunteer Locally',
    description: "Use your time and professional skills to support Big Love's mission and operations from home.",
    details: ['Fundraising Events: Help organize local dinners, auctions, and community events', 'Logistics & Admin: Support behind-the-scenes work like record-keeping and donor outreach', 'Creative Skills: Use graphic design, photography, or social media to share the Big Love story'],
    cta: 'Volunteer Your Skills',
    href: '#volunteer'
  }, {
    icon: <DollarSign size={48} data-id="element-194" />,
    title: 'Give',
    description: 'Fund life-changing projects that bring water, power, hope, and the Gospel to communities in need.',
    details: ['One-time or monthly giving', 'Fund specific projects', 'Join The Big Love Circle', '100% tax-deductible'],
    cta: 'Start Giving',
    href: '/donate'
  }, {
    icon: <Plane size={48} data-id="element-195" />,
    title: 'Go',
    description: 'Join a mission trip to Uganda or the Philippines and experience transformation firsthand.',
    details: ['Hands-on service projects', 'Gospel outreach opportunities', 'Cultural immersion', 'Life-changing experience'],
    cta: 'Explore Trips',
    href: '/trips'
  }];
  return <div className="pt-20" data-id="element-196">
      {/* Hero */}
      <section className="bg-[var(--color-text)] text-white py-20 relative" data-id="element-197">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1509099836639-18ba1795216d?w=1600)'
      }} data-id="element-198"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-199">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-200">
            <h1 className="text-white mb-6" data-id="element-201">Welcome</h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-202">
              At Big Love Foundation, we believe everyone can be a part of this
              story. Whether you're praying, volunteering, or serving on a trip,
              your contribution helps us{' '}
              <strong data-id="element-203">love God, love ourselves, and love others.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Options */}
      <section className="py-20 bg-[var(--color-gray-light)]" data-id="element-204">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-205">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-206">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-207">
              Be a Big Love Hero
            </h2>
          </motion.div>

          <div className="space-y-12" data-id="element-208">
            {options.map((option, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-209">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 lg:p-12" data-id="element-210">
                  <div className="flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-211">
                    <div className="text-[var(--color-primary)] mb-4" data-id="element-212">
                      {option.icon}
                    </div>
                    <h2 className="text-3xl text-[var(--color-text)] mb-4 font-bold" data-id="element-213">
                      {option.title}
                    </h2>
                    <p className="text-base text-[var(--color-text-light)] leading-relaxed" data-id="element-214">
                      {option.description}
                    </p>
                  </div>

                  <div className="lg:col-span-2" data-id="element-215">
                    <ul className="space-y-3 mb-8" data-id="element-216">
                      {option.details.map((detail, i) => {
                    const [boldPart, rest] = detail.split(': ');
                    return <li key={i} className="flex items-start" data-id="element-217">
                            <span className="text-[var(--color-primary)] mr-3 text-xl" data-id="element-218">
                              •
                            </span>
                            <span className="text-[var(--color-text-light)]" data-id="element-219">
                              {rest ? <>
                                  <strong className="text-[var(--color-text)]" data-id="element-220">
                                    {boldPart}:
                                  </strong>{' '}
                                  {rest}
                                </> : detail}
                            </span>
                          </li>;
                  })}
                    </ul>
                    <Button variant="primary" size="lg" href={option.href} data-id="element-221">
                      {option.cta}
                    </Button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-id="element-222">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-223">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-224">
            <h2 className="text-[var(--color-text)] mb-6" data-id="element-225">
              Ready To Get Started?
            </h2>
            <p className="text-lg text-[var(--color-text-light)] mb-8" data-id="element-226">
              Your involvement matters. Whether you give, go, pray, serve, or
              share — you're part of the transformation.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-227">
              <Button variant="primary" size="lg" href="/donate" data-id="element-228">
                Donate Now
              </Button>
              <Button variant="outline" size="lg" href="/trips" data-id="element-229">
                Join a Trip
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}